import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { router } from '../../router';
import { t } from '../../i18n';
import { modulePortalWidgets } from '../../modules/registry';

interface PortalClass { id: string; name: string; day_of_week: string; start_time: string; end_time: string; teacher_name: string | null; sessions: any[]; }
interface PortalEvent { id: string; name: string; type_name: string | null; start_date: string | null; end_date: string | null; my_status: string | null; }

/** Student portal home: upcoming classes, levels, and placeholder widgets. */
@customElement('cadence-portal-dashboard')
export class CadencePortalDashboard extends LitElement {
  @state() private classes: PortalClass[] = [];
  @state() private levels: any[] = [];
  @state() private events: PortalEvent[] = [];
  @state() private loading = true;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 4px; font-size: 1.6rem; }
    p.sub { margin: 0 0 var(--space-5); color: var(--color-text-secondary); }
    .grid { display: grid; grid-template-columns: 2fr 1fr; gap: var(--space-4); align-items: start; }
    @media (max-width: 860px) { .grid { grid-template-columns: 1fr; } }
    .col { display: grid; gap: var(--space-4); }
    .session { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--color-border); }
    .session:last-child { border-bottom: none; }
    .session .when { font-weight: 600; }
    .session .meta { color: var(--color-text-secondary); font-size: 0.82rem; }
    .levels { display: flex; flex-direction: column; gap: 10px; }
    .level { display: flex; align-items: center; justify-content: space-between; }
    .muted { color: var(--color-text-secondary); font-size: 0.9rem; }
    .links { display: flex; flex-direction: column; gap: 8px; }
    .links a { color: var(--color-primary); text-decoration: none; }
    .links a:hover { text-decoration: underline; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const [classes, levels, events] = await Promise.all([
        api.get<PortalClass[]>('/portal/classes'),
        api.get<any[]>('/portal/levels'),
        api.get<PortalEvent[]>('/portal/events').catch(() => []),
      ]);
      this.classes = classes; this.levels = levels; this.events = events;
    } finally { this.loading = false; }
  }

  /** Events that aren't in the past (undated events are treated as upcoming). */
  private get upcomingEvents() {
    const today = new Date().toISOString().slice(0, 10);
    return this.events
      .filter((e) => !e.start_date || (e.end_date ?? e.start_date) >= today)
      .sort((a, b) => (a.start_date ?? '9999').localeCompare(b.start_date ?? '9999'))
      .slice(0, 6);
  }

  private eventDate(e: PortalEvent) {
    if (!e.start_date) return t('portal.common.dateTbc');
    return e.end_date && e.end_date !== e.start_date ? `${e.start_date} → ${e.end_date}` : e.start_date;
  }

  private eventBadge(e: PortalEvent) {
    if (e.my_status === 'confirmed') return html`<cadence-badge color="var(--color-success)">${t('portal.dashboard.eventConfirmed')}</cadence-badge>`;
    if (e.my_status === 'interested') return html`<cadence-badge color="var(--color-primary)">${t('portal.dashboard.eventPending')}</cadence-badge>`;
    if (e.my_status === 'denied') return html`<cadence-badge color="var(--color-danger)">${t('portal.dashboard.eventNotApproved')}</cadence-badge>`;
    return html`<cadence-badge variant="soft">${t('portal.dashboard.eventOpen')}</cadence-badge>`;
  }

  /** Flatten upcoming sessions across classes within the next 7 days. */
  private get upcoming() {
    const horizon = new Date(); horizon.setDate(horizon.getDate() + 7);
    const limit = horizon.toISOString().slice(0, 10);
    const rows: any[] = [];
    for (const c of this.classes) {
      for (const s of c.sessions ?? []) {
        if (s.date <= limit) rows.push({ ...s, className: c.name, teacher: c.teacher_name, start_time: c.start_time });
      }
    }
    return rows.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 8);
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    const name = store.get().user?.first_name || '';
    return html`
      <cadence-break-banner></cadence-break-banner>
      <h1>${t('common.portal.welcome', { name })}</h1>
      <p class="sub">${t('common.app.tagline')}</p>
      <div class="grid">
        <div class="col">
          <cadence-card heading=${t('common.portal.upcomingClasses')}>
            ${this.upcoming.length
              ? this.upcoming.map((s) => html`
                  <div class="session">
                    <div>
                      <div class="when">${s.className}</div>
                      <div class="meta">${s.date} · ${s.start_time ?? ''} ${s.teacher ? '· ' + s.teacher : ''}</div>
                    </div>
                    ${s.status === 'cancelled'
                      ? html`<cadence-badge color="var(--color-danger)">${t('portal.common.cancelled')}</cadence-badge>`
                      : html`<cadence-badge color=${this.statusColor(s.attendance_status)}>${this.statusLabel(s.attendance_status)}</cadence-badge>`}
                  </div>`)
              : html`<p class="muted">${t('common.portal.noClasses')}</p>`}
          </cadence-card>

          <cadence-card heading=${t('common.portal.levels')}>
            ${this.levels.length
              ? html`<div class="levels">${this.levels.map((l) => html`
                  <div class="level"><cadence-badge variant="soft">${l.level_name}</cadence-badge><span>${l.dance_type_name}</span></div>`)}</div>`
              : html`<p class="muted">${t('common.portal.noLevels')}</p>`}
          </cadence-card>
        </div>

        <div class="col">
          <cadence-card heading=${t('common.portal.payments')}>
            <p class="muted">${t('common.portal.paymentsEmpty')}</p>
          </cadence-card>
          <cadence-card heading=${t('common.portal.events')}>
            ${this.upcomingEvents.length
              ? this.upcomingEvents.map((e) => html`
                  <div class="session" style="cursor:pointer" @click=${() => router.navigate(`/portal/events/${e.id}`)}>
                    <div>
                      <div class="when">${e.name}</div>
                      <div class="meta">${this.eventDate(e)}${e.type_name ? ' · ' + e.type_name : ''}</div>
                    </div>
                    ${this.eventBadge(e)}
                  </div>`)
              : html`<p class="muted">${t('common.portal.eventsEmpty')}</p>`}
          </cadence-card>
          ${modulePortalWidgets().map((w) => w.render())}
          <cadence-card heading=${t('common.portal.quickLinks')}>
            <div class="links">
              <a href="/portal/profile" @click=${(e: Event) => { e.preventDefault(); router.navigate('/portal/profile'); }}>${t('common.portalNav.profile')}</a>
              <a href="/portal/classes" @click=${(e: Event) => { e.preventDefault(); router.navigate('/portal/classes'); }}>${t('common.portalNav.classes')}</a>
              <a href="/portal/contract" @click=${(e: Event) => { e.preventDefault(); router.navigate('/portal/contract'); }}>${t('common.portalNav.contract')}</a>
            </div>
          </cadence-card>
        </div>
      </div>
    `;
  }

  private statusColor(s: string) {
    return s === 'attending' ? 'var(--color-success)' : s === 'not_attending' ? 'var(--color-text-secondary)' : s === 'absent' ? 'var(--color-danger)' : 'var(--color-primary)';
  }
  private statusLabel(s: string) {
    return s === 'attending' ? t('common.portal.attending') : s === 'not_attending' ? t('common.portal.notAttending') : s === 'absent' ? t('common.portal.absent') : '—';
  }
}
